/**
 * Small time-travel hook for the Bulk Photo Sorter. Each mutation captures a
 * snapshot of the tracked state before applying so cmd-Z can walk backwards
 * through split/merge/drop/drag/attach actions.
 *
 * Scope of what's tracked: only mutations the user visibly triggered. Transient
 * things like selection or the "saving…" flag are excluded.
 *
 * Server-side undo: some mutations (attach) write to the backend. The optional
 * `rollback` function on a snapshot is awaited when that snapshot is undone —
 * useful for calling PUT /inventory/{id} with the pre-attach photos array.
 */
import { useCallback, useRef, useState } from "react";

const MAX_HISTORY = 30;

/**
 * useUndoStack(getSnapshot, applySnapshot) — external state contract.
 *   getSnapshot()          → returns a serialisable copy of current state
 *   applySnapshot(snap)    → restores state from a previously-captured copy
 * The hook owns only the history array + cursor; the caller keeps their own
 * useState for the tracked values so nothing gets duplicated.
 */
export function useUndoStack({ getSnapshot, applySnapshot }) {
  const stackRef = useRef([]);                    // [{ label, before, rollback? }]
  const redoRef = useRef([]);                     // [{ label, after, redoServer? }]
  const inFlightRef = useRef(false);              // guard reentry during undo/redo

  const push = useCallback((label, rollback) => {
    if (inFlightRef.current) return;
    const snap = getSnapshot();
    stackRef.current.push({ label, before: snap, rollback });
    if (stackRef.current.length > MAX_HISTORY) stackRef.current.shift();
    // A fresh mutation invalidates the redo lane, standard editor behaviour.
    redoRef.current = [];
  }, [getSnapshot]);

  const undo = useCallback(async () => {
    if (stackRef.current.length === 0 || inFlightRef.current) return null;
    inFlightRef.current = true;
    try {
      const entry = stackRef.current.pop();
      const currentSnap = getSnapshot();
      if (entry.rollback) {
        try { await entry.rollback(); }
        catch { /* server rollback failed; still restore client state so user isn't stuck */ }
      }
      applySnapshot(entry.before);
      // Preserve the original rollback so a subsequent undo→redo→undo still
      // triggers server-side reconciliation (attach rollback). `after` is the
      // client state to restore when redo is invoked.
      redoRef.current.push({ label: entry.label, after: currentSnap, rollback: entry.rollback });
      return entry.label;
    } finally {
      inFlightRef.current = false;
    }
  }, [getSnapshot, applySnapshot]);

  const redo = useCallback(async () => {
    if (redoRef.current.length === 0 || inFlightRef.current) return null;
    inFlightRef.current = true;
    try {
      const entry = redoRef.current.pop();
      const beforeRedo = getSnapshot();
      applySnapshot(entry.after);
      // Push back onto the undo stack so cmd-Z after a cmd-shift-Z walks
      // history in the expected direction.
      stackRef.current.push({ label: entry.label, before: beforeRedo, rollback: entry.rollback });
      if (stackRef.current.length > MAX_HISTORY) stackRef.current.shift();
      return entry.label;
    } finally {
      inFlightRef.current = false;
    }
  }, [getSnapshot, applySnapshot]);

  const clear = useCallback(() => {
    stackRef.current = [];
    redoRef.current = [];
  }, []);

  // -------- Consolidated stack sizes --------------------------------------
  // Two separate useState counters (one for undo, one for redo) drift apart
  // during React 19's transitional renders — a click can trigger a render
  // where redoSize=1 but redoRef.current is already empty, causing
  // `nextRedoLabel` to deref undefined and crash the sorter into its error
  // boundary. Keeping both counters in a single object updated in a single
  // synchronous commit keeps them consistent within any given render.
  const [sizes, setSizes] = useState({ undo: 0, redo: 0 });
  const syncSizes = useCallback(() => {
    setSizes({ undo: stackRef.current.length, redo: redoRef.current.length });
  }, []);

  const wrapPush = useCallback((label, rollback) => {
    push(label, rollback);
    syncSizes();
  }, [push, syncSizes]);

  const wrapUndo = useCallback(async () => {
    const label = await undo();
    syncSizes();
    return label;
  }, [undo, syncSizes]);

  const wrapRedo = useCallback(async () => {
    const label = await redo();
    syncSizes();
    return label;
  }, [redo, syncSizes]);

  const wrapClear = useCallback(() => {
    clear();
    syncSizes();
  }, [clear, syncSizes]);

  return {
    push: wrapPush,
    undo: wrapUndo,
    redo: wrapRedo,
    clear: wrapClear,
    canUndo: sizes.undo > 0,
    canRedo: sizes.redo > 0,
    // Defensive `?.` — even with consolidated state, guard against any future
    // race where the counter is truthy but the ref has been drained.
    lastLabel: stackRef.current[stackRef.current.length - 1]?.label || null,
    nextRedoLabel: redoRef.current[redoRef.current.length - 1]?.label || null,
    depth: sizes.undo,
    redoDepth: sizes.redo,
  };
}
